import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Grid,
  MenuItem,
  Select,
  Paper,
  CircularProgress,
  Alert,
  Tooltip,
  Button,
  IconButton,
  tableCellClasses,
  Autocomplete,
  Chip,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import LoadingButton from '@mui/lab/LoadingButton';
import styled_ from '@emotion/styled';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchRateListReport,
  downloadRateListReport
} from '../../store/reportStore/reportThunk';
import { RateListItem, RateListResponse, InventoryReportFetch } from '../../utils/report.types';
import { ApiStatus } from '../../utils/api.types';
import { toast } from 'react-toastify';
import { Utils } from '../../utils/common-utils';
import ReusableReportHeader from './ReusableReportHeader';
import { IOptions } from '../FormFields/CustomSelect';

// Styled components - GSTR-2 Purchase Summary Style
const StyledTableCell = styled_(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#eaecf0',
    color: '#000',
    border: '0.75px solid #d9d4d4',
    margin: 0,
    padding: 5,
    fontSize: '9pt',
    paddingLeft: '15px !important',
  },
  [`&.${tableCellClasses.footer}`]: {
    backgroundColor: '#f5f5f5',
    color: 'black',
    borderTop: '0.25px solid #d9d4d4',
    borderBottom: '0.25px solid #d9d4d4',
    left: 0,
    bottom: 0,
    zIndex: 2,
    position: 'sticky'
  },
  [`&.${tableCellClasses.body}`]: {
    verticalAlign: 'super',
    fontSize: '9pt',
    fontWeight: 'normal',
    color: '#292d35',
    padding: 5,
    border: '0.75px solid #d9d4d4',
    paddingLeft: '15px !important',
    margin: 0
  },
}));

const StyledTableRow = styled_(TableRow)(({ theme }) => ({
  // Using the same empty style as GSTR-2 report
}));

interface RateListProps {
  type: string;
  rawData?: any; // Can be RateListResponse or new post format
}

const RateList: React.FC<RateListProps> = ({ type, rawData }) => {
  const dispatch = useAppDispatch();
  const { status } = useAppSelector(state => state.reports);
  const { selectedAccount } = useAppSelector(state => state.auth);
  
  // State management
  const [reportData, setReportData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState('');
  const [searchOptions, setSearchOptions] = useState<any[]>([]);
  const [selectedSearchItem, setSelectedSearchItem] = useState<any>(null);
  const [sortBy, setSortBy] = useState('itemName');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [visibleColumns, setVisibleColumns] = useState({
    mrp: true,
    salePrice: true,
    purchasePrice: true
  });


  // Create filter string
  const createFilter = useCallback(() => {
    let filterStr = '';

    if (selectedSearchItem) {
      filterStr += `search=${encodeURIComponent(selectedSearchItem.name)}&`;
    }

    filterStr += `sortBy=${sortBy}&sortOrder=${sortOrder}`;

    return filterStr;
  }, [selectedSearchItem, sortBy, sortOrder]);

  // Update filters when dependencies change
  useEffect(() => {
    const newFilters = createFilter();
    setFilters(newFilters);
  }, [createFilter]);

  // Initialize filters on component mount
  useEffect(() => {
    if (!filters) {
      const initialFilters = createFilter();
      setFilters(initialFilters);
    }
  }, []);

  // Fetch data function
  const fetchData = useCallback(async () => {
    if (!selectedAccount?._id) return;

    setIsLoading(true);

    const params: InventoryReportFetch = {
      accountId: selectedAccount._id,
      search: selectedSearchItem?.name || undefined,
      sortBy,
      sortOrder
    };

    console.log('Fetching with params:', params);

    try {
      const response = await dispatch(fetchRateListReport(params)).unwrap();
      setReportData(response);
      console.log('Response received:', response);
    } catch (error: any) {
      console.error('Fetch error:', error);
      toast.error(error.message || 'Failed to fetch rate list report');
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, selectedAccount, selectedSearchItem, sortBy, sortOrder]);

  // Load data on component mount or filter changes
  useEffect(() => {
    if (rawData) {
      setReportData(rawData);
      setIsLoading(false);
    } else {
      fetchData();
    }
  }, [rawData, fetchData]);

  // Set search options for autocomplete
  useEffect(() => {
    const items = reportData?.data?.items || reportData?.reportData || [];
    if (items.length > 0) {
      const searchItems = items.map((item: any) => ({
        _id: item._id,
        name: item.itemName,
        code: item.itemCode,
        category: item.categoryName
      }));
      setSearchOptions(searchItems);
    }
  }, [reportData]);

  // Handle download functions
  const handleDownloadPDF = async () => {
    if (!selectedAccount?._id) return;

    const params: InventoryReportFetch = {
      accountId: selectedAccount._id,
      search: selectedSearchItem?.name || undefined,
      sortBy,
      sortOrder,
      showMrp: visibleColumns.mrp.toString(),
      showSalePrice: visibleColumns.salePrice.toString(),
      showPurchasePrice: visibleColumns.purchasePrice.toString(),
      isDownloadPdf: 'true'
    };

    try {
      const response = await dispatch(downloadRateListReport(params)).unwrap();
      if (response) {
        const blob = new Blob([response], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `rate_list_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("PDF download completed");
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to download PDF');
    }
  };

  const handleDownloadExcel = async () => {
    if (!selectedAccount?._id) return;

    const params: InventoryReportFetch = {
      accountId: selectedAccount._id,
      search: selectedSearchItem?.name || undefined,
      sortBy,
      sortOrder,
      showMrp: visibleColumns.mrp.toString(),
      showSalePrice: visibleColumns.salePrice.toString(),
      showPurchasePrice: visibleColumns.purchasePrice.toString(),
      isDownloadExcel: 'true'
    };

    try {
      const response = await dispatch(downloadRateListReport(params)).unwrap();
      if (response) {
        const blob = new Blob([response], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `rate_list_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Excel download completed");
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to download Excel');
    }
  };

  // Handle filter functions
  const handleRefreshFilters = () => {
    setSelectedSearchItem(null);
    setSortBy('itemName');
    setSortOrder('asc');
  };

  // Handle search item selection
  const handleSearchItemChange = (event: any, value: any) => {
    setSelectedSearchItem(value);
  };




  return (
    <Box sx={{ width: 'calc(100% - 7px)', overflow: 'hidden', mb: 2 }} className={'c-card2'}>
      {!isLoading && status !== ApiStatus.LOADING ? (
        <>
          {/* Report Header */}
          <ReusableReportHeader
            reportType="RATE_LIST"
            title="Rate List Report"
            subtitle="Current pricing and stock information for all items"
            recordCount={reportData?.total || reportData?.data?.summary?.totalItems || 0}
            isLoading={isLoading}
            onDownloadPDF={handleDownloadPDF}
            onDownloadExcel={handleDownloadExcel}
            showPrint={false}
            showShare={false}
          />

          <div style={{ marginBottom: 12 }} id='pdfContainer'>
            {/* Header and Summary Section - GSTR-2 Style */}
            <Grid container p={2} style={{ borderBottom: '.5px solid #9da7b7', marginBottom: 0 }}>
              <Grid xs={9} style={{ textAlign: 'left' }}>
                
                {/* Filters - Party Ledger Style Exact Copy */}
                <Box style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <IconButton onClick={handleRefreshFilters}
                    style={{ marginRight: '15px',  color: '#488dff', backgroundColor: '#fff', border: '3px solid #e9ecf1' }}
                    size='small' ><RefreshIcon /></IconButton>

                  <Autocomplete
                    disablePortal
                    id="search-items"
                    value={selectedSearchItem || null}
                    style={{ width: 300, marginRight: '10px' }}
                    onChange={handleSearchItemChange}
                    options={searchOptions}
                    getOptionLabel={(option) => option.name || ''}
                    renderInput={(params) => <TextField {...params}
                      variant="standard"
                      name="search"
                      size='small'
                      placeholder="Type here to search items ..."
                      InputProps={{ ...params.InputProps, disableUnderline: true }}
                      className='c-text-field-3'
                    />}
                  />

                  <Select
                    name="sort"
                    disableUnderline
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          border: "1.5px solid #e9ecf1",
                          boxShadow: 'none',
                          borderRadius: '5px'
                        }
                      }
                    }}
                    value={`${sortBy}-${sortOrder}`}
                    onChange={(e) => {
                      const [field, order] = e.target.value.split('-');
                      setSortBy(field);
                      setSortOrder(order as 'asc' | 'desc');
                    }}
                    sx={{
                      boxShadow: "none",
                      width: 200,
                      marginRight: '10px',
                      '& .MuiOutlinedInput-input': { p: 0, m: 0 },
                      '& .MuiSelect-select': { p: 0 },
                      ".MuiOutlinedInput-notchedOutline": { border: 0 },
                      "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                      {
                        border: 0,
                      },
                      "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                      {
                        border: 0,
                      },
                    }}
                    label="Sort Options"
                    className='c-text-field-3'
                    variant="outlined"
                  >
                    <MenuItem value="itemName-asc">Name: A to Z</MenuItem>
                    <MenuItem value="itemName-desc">Name: Z to A</MenuItem>
                    <MenuItem value="salePrice-asc">Sale Price: Low to High</MenuItem>
                    <MenuItem value="salePrice-desc">Sale Price: High to Low</MenuItem>
                    <MenuItem value="purchasePrice-asc">Purchase Price: Low to High</MenuItem>
                    <MenuItem value="purchasePrice-desc">Purchase Price: High to Low</MenuItem>
                    <MenuItem value="mrp-asc">MRP: Low to High</MenuItem>
                    <MenuItem value="mrp-desc">MRP: High to Low</MenuItem>
                  </Select>

                  {/* Column visibility multi-select dropdown */}
                  <Select
                    multiple
                    disableUnderline
                    value={Object.entries(visibleColumns).filter(([_, visible]) => visible).map(([key, _]) => key)}
                    onChange={(e) => {
                      const selectedValues = e.target.value as string[];
                      setVisibleColumns({
                        mrp: selectedValues.includes('mrp'),
                        salePrice: selectedValues.includes('salePrice'),
                        purchasePrice: selectedValues.includes('purchasePrice')
                      });
                    }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {(selected as string[]).map((value) => (
                          <Chip
                            key={value}
                            label={value === 'mrp' ? 'MRP' : value === 'salePrice' ? 'Sale Price' : 'Purchase Price'}
                            size="small"
                            sx={{ height: '20px' }}
                          />
                        ))}
                      </Box>
                    )}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          border: "1.5px solid #e9ecf1",
                          boxShadow: 'none',
                          borderRadius: '5px'
                        }
                      }
                    }}
                    sx={{
                      boxShadow: "none",
                      width: 280,
                      marginLeft: '20px',
                      '& .MuiOutlinedInput-input': { p: 0, m: 0 },
                      '& .MuiSelect-select': { p: 0 },
                      ".MuiOutlinedInput-notchedOutline": { border: 0 },
                      "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                      {
                        border: 0,
                      },
                      "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                      {
                        border: 0,
                      },
                    }}
                    className='c-text-field-3'
                    variant="outlined"
                  >
                    <MenuItem value="mrp">
                      <Checkbox checked={visibleColumns.mrp} />
                      MRP
                    </MenuItem>
                    <MenuItem value="salePrice">
                      <Checkbox checked={visibleColumns.salePrice} />
                      Sale Price
                    </MenuItem>
                    <MenuItem value="purchasePrice">
                      <Checkbox checked={visibleColumns.purchasePrice} />
                      Purchase Price
                    </MenuItem>
                  </Select>
                </Box>
              </Grid>
              {/* <Grid xs={2} >
                <LoadingButton
                  className={'buttonContainedRoot'}
                  size='small'
                  type="button"
                  name={'submit'}
                  onClick={() => {}}
                  loading={isLoading}
                  variant="contained"
                  sx={{ minWidth: 160 }}
                >
                  Send To Account Email
                </LoadingButton>
              </Grid> */}
            </Grid>

              {/* Right side summary section - Party Ledger Style */}
              {/* <Grid xs={3} style={{ textAlign: 'right' }}>
                <Typography style={{fontSize: 14, fontWeight: 400, marginBottom: '6px'}}>
                  <strong style={{paddingRight: 10}}>Total Items:</strong>
                  {reportData?.data?.summary?.totalItems || reportData?.total || 0}
                </Typography>
                <Typography style={{fontSize: 14, fontWeight: 400, marginBottom: '6px', color: '#2e7d32'}}>
                  <strong style={{paddingRight: 10}}>In Stock:</strong>
                  {reportData?.data?.summary?.inStockItems ||
                   (reportData?.reportData ? reportData.reportData.filter((item: any) => item.stockStatus === 'In Stock').length : 0)}
                </Typography>
                <Typography style={{fontSize: 14, fontWeight: 400, marginBottom: '6px', color: '#d32f2f'}}>
                  <strong style={{paddingRight: 10}}>Out of Stock:</strong>
                  {reportData?.data?.summary?.outOfStockItems ||
                   (reportData?.reportData ? reportData.reportData.filter((item: any) => item.stockStatus === 'Out of Stock').length : 0)}
                </Typography>
                {reportData?.data?.summary?.totalValue !== undefined && (
                  <Typography style={{
                    fontSize: 14,
                    fontWeight: 400,
                    marginBottom: '6px',
                    color: '#1976d2'
                  }}>
                    <strong style={{paddingRight: 10}}>Total Value:</strong>
                    {Utils.formatNumber(reportData.data.summary.totalValue || 0, true, true)}
                  </Typography>
                )}
              </Grid> */}

          {/* Data Table - GSTR-2 Purchase Summary Style */}
              <Table stickyHeader sx={{ minWidth: 700, marginBottom: '100px' }} size='small' aria-label="customized table">
            <TableHead>
              <TableRow>
                <StyledTableCell width={300}>Item Details</StyledTableCell>
                <StyledTableCell>Item Code</StyledTableCell>
                <StyledTableCell>HSN Code</StyledTableCell>
                {visibleColumns.mrp && <StyledTableCell>MRP</StyledTableCell>}
                {visibleColumns.salePrice && <StyledTableCell>Sale Price</StyledTableCell>}
                {visibleColumns.purchasePrice && <StyledTableCell>Purchase Price</StyledTableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <StyledTableCell colSpan={3 + Object.values(visibleColumns).filter(Boolean).length} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Loading...
                    </Typography>
                  </StyledTableCell>
                </TableRow>
              ) : (reportData?.data?.items?.length || reportData?.reportData?.length) ? (
                (reportData?.data?.items || reportData?.reportData || []).map((item: any) => (
                  <StyledTableRow key={item._id}>
                    <StyledTableCell>{item.itemName}</StyledTableCell>
                    <StyledTableCell>{item.itemCode || '-'}</StyledTableCell>
                    <StyledTableCell>{item.hsnCode || '-'}</StyledTableCell>
                    {visibleColumns.mrp && <StyledTableCell>{Utils.formatNumber(item.mrp || 0, false, true)} {item.unitCode ? `/ ${item.unitCode}` : ''}</StyledTableCell>}
                    {visibleColumns.salePrice && <StyledTableCell>{Utils.formatNumber(item.salePrice || 0, false, true)} {item.unitCode ? `/ ${item.unitCode}` : ''}</StyledTableCell>}
                    {visibleColumns.purchasePrice && <StyledTableCell>{Utils.formatNumber(item.purchasePrice || 0, false, true)} {item.unitCode ? `/ ${item.unitCode}` : ''}</StyledTableCell>}
                  </StyledTableRow>
                ))
              ) : (
                <TableRow>
                  <StyledTableCell colSpan={3 + Object.values(visibleColumns).filter(Boolean).length} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No items found
                    </Typography>
                  </StyledTableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          </div>
        </>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          {status === ApiStatus.FAILED ? (
            <Alert severity="error" sx={{ m: 2 }}>
              Failed to load rate list report. Please try again.
            </Alert>
          ) : (
            <CircularProgress color="info" />
          )}
        </Box>
      )}
    </Box>
  );
};

export default RateList;